/**
 * Global Search Hooks
 * Story 11.5: Global Search
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

// ============== Types ==============

export interface SearchResult {
  id: string;
  type: string; // campaign, content, workflow, template, user
  title: string;
  description?: string;
  highlight?: string;
  score: number;
  metadata?: {
    status?: string;
    created_at?: string;
  };
}

export interface SearchResultGroup {
  entity_type: string;
  results: SearchResult[];
  count: number;
}

export interface SearchResponse {
  results: SearchResultGroup[];
  total: number;
  query_time_ms: number;
}

// ============== Hooks ==============

/**
 * Debounce hook - delays value update
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Global search hook
 */
export function useGlobalSearch(
  query: string,
  types?: string[],
  filters?: Record<string, any>,
  enabled: boolean = true
) {
  return useQuery<SearchResponse>({
    queryKey: ['global-search', query, types, filters],
    queryFn: async () => {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();

      params.append('q', query);

      if (types && types.length > 0) {
        params.append('type', types.join(','));
      }

      if (filters?.status) {
        params.append('status', filters.status);
      }

      const response = await fetch(
        `/api/v1/search?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      return response.json();
    },
    enabled: enabled && query.length >= 2, // Only search if query >= 2 chars
    staleTime: 60000, // Cache for 1 minute
  });
}

/**
 * Search history hook (stores recent searches in localStorage)
 */
export function useSearchHistory() {
  const [history, setHistory] = useState<string[]>([]);

  useEffect(() => {
    // Load history from localStorage
    const stored = localStorage.getItem('searchHistory');
    if (stored) {
      try {
        setHistory(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse search history', e);
      }
    }
  }, []);

  const addToHistory = (query: string) => {
    if (!query || query.length < 2) return;

    setHistory((prev) => {
      // Remove duplicates and add to front
      const newHistory = [query, ...prev.filter((q) => q !== query)].slice(0, 10);
      localStorage.setItem('searchHistory', JSON.stringify(newHistory));
      return newHistory;
    });
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('searchHistory');
  };

  return { history, addToHistory, clearHistory };
}

// ============== Utility Functions ==============

/**
 * Get icon for entity type
 */
export function getEntityTypeIcon(type: string): string {
  const iconMap: Record<string, string> = {
    campaign: 'Target',
    content: 'FileText',
    workflow: 'GitBranch',
    template: 'Layout',
    user: 'User',
  };
  return iconMap[type] || 'Search';
}

/**
 * Get color class for entity type
 */
export function getEntityTypeColor(type: string): string {
  const colorMap: Record<string, string> = {
    campaign: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
    content: 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30',
    workflow: 'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30',
    template: 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30',
    user: 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700',
  };
  return colorMap[type] || 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
}

/**
 * Get display name for entity type
 */
export function getEntityTypeDisplayName(type: string): string {
  const displayMap: Record<string, string> = {
    campaign: 'Campaigns',
    content: 'Content',
    workflow: 'Workflows',
    template: 'Templates',
    user: 'Team Members',
  };
  return displayMap[type] || type;
}

/**
 * Get resource link for search result
 */
export function getSearchResultLink(result: SearchResult): string {
  const linkMap: Record<string, string> = {
    campaign: `/campaigns/${result.id}`,
    content: `/content/${result.id}`,
    workflow: `/workflows/${result.id}`,
    template: `/templates/${result.id}`,
    user: `/team/${result.id}`,
  };
  return linkMap[result.type] || '#';
}

/**
 * Strip HTML tags from highlighted text (for plain text display)
 */
export function stripHighlightTags(text?: string): string {
  if (!text) return '';
  return text.replace(/<\/?em>/g, '');
}
